import React, { useCallback, useEffect, useRef, useState } from 'react';
import clinicalNoteService from './clinicalNoteService.js';
import './ClinicalNoteAttachedDailyCompact.css';

/**
 * Narrative attachment for an Initial Evaluation, Re-Evaluation or Progress Note.
 * Pain, ROM, Strength and any evaluation comments are read-only references to
 * the parent note; no additional measurement records are created.
 */
const EMPTY = { subjective: '', assessment: '', plan: '' };
const normalize = (value) => value == null ? '' : String(value);
const sideLabel = (value) => value && value !== 'NOT_APPLICABLE'
  ? `${String(value).toLowerCase().replace(/^./, (c) => c.toUpperCase())} — ` : '';

function ReadOnlyMeasurements({ detail, loading }) {
  const pain = detail?.pain;
  const rom = Array.isArray(detail?.rom) ? detail.rom : [];
  const strength = Array.isArray(detail?.strength) ? detail.strength : [];
  const evaluationHistory = detail?.evaluationHistory;
  const noteType = detail?.note?.noteType ?? detail?.clinicalNote?.noteType ?? detail?.noteType;
  const parentLabel = noteType === 'PROGRESS_NOTE' ? 'Progress Note' : 'Evaluation';

  if (loading) return <p>Loading clinical measurements…</p>;

  return (
    <div className="clinical-attached-daily-measurements">
      <p style={{ marginTop: 0, color: '#555' }}>
        These are the {parentLabel}'s measurements, displayed for reference. Edit them in
         the clinical sections above; this attachment cannot change them.
      </p>
      <div style={{ display: 'grid', gap: 12, gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))' }}>
        <section>
          <h4 style={{ margin: '0 0 6px' }}>Pain</h4>
          {pain ? (
            <p style={{ margin: 0 }}>
              Best: {pain.bestPain ?? '—'} · Current: {pain.currentPain ?? '—'} · Worst: {pain.worstPain ?? '—'}
              {pain.painComments ? <span style={{ display: 'block' }}>Comments: {pain.painComments}</span> : null}
            </p>
          ) : <p style={{ margin: 0 }}>No saved pain documentation.</p>}
        </section>
        <section>
          <h4 style={{ margin: '0 0 6px' }}>Range of Motion</h4>
          {rom.length ? <ul style={{ margin: 0, paddingLeft: 18 }}>
            {rom.map((item, index) => <li key={`${item.romTemplateId}-${item.measurementSide}-${index}`}>
              {sideLabel(item.measurementSide)}{item.displayName ?? 'ROM'}: {item.measurementValue ?? '—'}{item.unit ? ` ${item.unit}` : ''}
              {item.comments ? ` — ${item.comments}` : ''}
            </li>)}
          </ul> : <p style={{ margin: 0 }}>No ROM fields available.</p>}
        </section>
        <section>
          <h4 style={{ margin: '0 0 6px' }}>Strength / GMMT</h4>
          {strength.length ? <ul style={{ margin: 0, paddingLeft: 18 }}>
            {strength.map((item, index) => <li key={`${item.strengthTemplateId}-${item.measurementSide}-${index}`}>
              {sideLabel(item.measurementSide)}{item.displayName ?? 'Strength'}: {item.strengthGradeDisplay ?? item.strengthGrade ?? '—'}
              {item.comments ? ` — ${item.comments}` : ''}
            </li>)}
          </ul> : <p style={{ margin: 0 }}>No Strength fields available.</p>}
        </section>
      </div>
      {evaluationHistory?.romStrengthComments ? (
        <p style={{ marginBottom: 0 }}><strong>ROM / Strength comments:</strong> {evaluationHistory.romStrengthComments}</p>
      ) : null}
    </div>
  );
}

export default function ClinicalNoteAttachedDailySection({
  clinicalNoteId,
  disabled = false,
  onSaved,
  onVersionChange,
  navigationCoordinator,
}) {
  const [fields, setFields] = useState(EMPTY);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [reloadToken, setReloadToken] = useState(0);
  const [expandedNarrative, setExpandedNarrative] = useState({ subjective: true, assessment: false, plan: false });
  useEffect(() => {
    setExpandedNarrative({ subjective: true, assessment: false, plan: false });
  }, [clinicalNoteId]);
  const fieldsRef = useRef(EMPTY);
  const timerRef = useRef(null);
  const readyRef = useRef(false);
  const savingRef = useRef(false);
  const inFlightRef = useRef(null);
  const failedRef = useRef(false);
  const errorRef = useRef('');
  const dirtyRef = useRef(false);
  const versionRef = useRef(null);
  const latestSavedRef = useRef(EMPTY);
  const mountedRef = useRef(true);
  const noteType = detail?.note?.noteType ?? detail?.clinicalNote?.noteType ?? detail?.noteType;
  const parentLabel = noteType === 'PROGRESS_NOTE' ? 'Progress Note' : 'Evaluation';
  const activeNoteIdRef = useRef(String(clinicalNoteId ?? ''));
  activeNoteIdRef.current = String(clinicalNoteId ?? '');
  const onSavedRef = useRef(onSaved);
  const onVersionChangeRef = useRef(onVersionChange);
  onSavedRef.current = onSaved;
  onVersionChangeRef.current = onVersionChange;

  const showError = useCallback((value) => {
    errorRef.current = value;
    if (mountedRef.current) setError(value);
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const load = useCallback(async () => {
    if (!clinicalNoteId) {
      readyRef.current = false;
      showError('Clinical note ID is required.');
      setLoading(false);
      return;
    }
    // Measurement refresh must not replace a pending or failed narrative draft.
    if (dirtyRef.current || savingRef.current) return;
    const requestedId = String(clinicalNoteId);
    readyRef.current = false;
    setLoading(true);
    showError('');
    try {
      const [attached, completeDetail] = await Promise.all([
        clinicalNoteService.getAttachedDailyNote(clinicalNoteId),
        clinicalNoteService.getClinicalNoteDetail(clinicalNoteId),
      ]);
      if (!mountedRef.current || activeNoteIdRef.current !== requestedId) return;
      // A user edit that occurred since the request began must never be overwritten.
      if (dirtyRef.current || savingRef.current) return;
      const next = {
        subjective: normalize(attached.subjective),
        assessment: normalize(attached.assessment),
        plan: normalize(attached.plan),
      };
      fieldsRef.current = next;
      latestSavedRef.current = next;
      dirtyRef.current = false;
      failedRef.current = false;
      // Do not invent an optimistic-lock version. Fresh parent versions are
      // obtained just before each mutation, but the initial response must
      // also contain a real version for safe note navigation.
      const loadedVersion = attached?.clinicalNoteVersion ??
        completeDetail?.note?.entityVersion ?? completeDetail?.clinicalNote?.entityVersion ??
        completeDetail?.entityVersion;
      if (loadedVersion == null) throw new Error('The clinical note version could not be loaded.');
      versionRef.current = loadedVersion;
      setFields(next);
      setDetail(completeDetail);
      setMessage('');
      readyRef.current = true;
    } catch (err) {
      if (mountedRef.current && activeNoteIdRef.current === requestedId) showError(err?.message || 'Unable to load the attached Daily Note.');
    } finally {
      if (mountedRef.current && activeNoteIdRef.current === requestedId) setLoading(false);
    }
  }, [clinicalNoteId, showError]);

  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    void load();
  }, [load, reloadToken]);

  // Return the SAME promise to concurrent callers, including visit navigation.
  // Every later edit is included before this promise settles.
  const save = useCallback(() => {
    if (inFlightRef.current) return inFlightRef.current;
    if (disabled || !readyRef.current || !dirtyRef.current || failedRef.current) {
      return Promise.resolve();
    }
    const task = (async () => {
      savingRef.current = true;
      if (mountedRef.current) setSaving(true);
      showError('');
      try {
        while (mountedRef.current && dirtyRef.current) {
          const snapshot = { ...fieldsRef.current };
          // A different editor may have saved since this attachment loaded.
          // Request the actual parent version immediately before the write.
          const currentNote = await clinicalNoteService.getClinicalNote(clinicalNoteId);
          const expectedVersion = currentNote?.entityVersion ??
            currentNote?.note?.entityVersion ?? currentNote?.clinicalNote?.entityVersion;
          if (expectedVersion == null) {
            throw new Error('The current clinical note version is unavailable. Nothing was saved.');
          }
          versionRef.current = expectedVersion;
          const response = await clinicalNoteService.saveAttachedDailyNote(clinicalNoteId, {
            ...snapshot,
            expectedVersion,
          });
          // Keep the optimistic-lock version current before the next mutation.
          const updatedNote = await clinicalNoteService.getClinicalNote(clinicalNoteId);
          const updatedVersion = updatedNote?.entityVersion ??
            updatedNote?.note?.entityVersion ?? updatedNote?.clinicalNote?.entityVersion ??
            response?.clinicalNoteVersion;
          if (updatedVersion == null) {
            throw new Error('Attached Daily Note saved, but the updated version could not be verified. Reload before retrying.');
          }
          if (!mountedRef.current || activeNoteIdRef.current !== String(clinicalNoteId)) {
            throw new Error('The clinical note changed during the save.');
          }
          versionRef.current = updatedVersion;
          latestSavedRef.current = snapshot;
          dirtyRef.current = Object.keys(snapshot).some((key) => fieldsRef.current[key] !== snapshot[key]);
          onVersionChangeRef.current?.(updatedVersion, updatedNote);
          onSavedRef.current?.(response, updatedNote);
          if (mountedRef.current) setMessage(dirtyRef.current ? 'Unsaved changes' : 'Saved');
        }
        failedRef.current = false;
      } catch (err) {
        failedRef.current = true;
        showError(err?.message || 'Attached Daily Note could not be saved.');
        if (mountedRef.current) setMessage('');
      } finally {
        savingRef.current = false;
        if (mountedRef.current) setSaving(false);
      }
    })();
    inFlightRef.current = task;
    // A cleanup continuation must not create an unhandled rejection.
    void task.then(
      () => { if (inFlightRef.current === task) inFlightRef.current = null; },
      () => { if (inFlightRef.current === task) inFlightRef.current = null; }
    );
    return task;
  }, [clinicalNoteId, disabled, showError]);

  const saveRef = useRef(save);
  saveRef.current = save;

  useEffect(() => {
    if (!navigationCoordinator || !clinicalNoteId) return undefined;
    return navigationCoordinator.register(clinicalNoteId, 'attached-daily', {
      flush: async () => {
        if (timerRef.current) {
          clearTimeout(timerRef.current);
          timerRef.current = null;
        }
        if (inFlightRef.current) await inFlightRef.current;
        if (dirtyRef.current && !failedRef.current) await saveRef.current();
      },
      getState: () => ({
        dirty: dirtyRef.current,
        saving: savingRef.current || Boolean(inFlightRef.current),
        invalid: false,
        error: errorRef.current || (!readyRef.current ? 'Attached Daily Note has not loaded successfully.' : ''),
      }),
    });
  }, [clinicalNoteId, navigationCoordinator]);

  const updateField = (key, value) => {
    if (disabled || !readyRef.current) return;
    const next = { ...fieldsRef.current, [key]: value };
    fieldsRef.current = next;
    dirtyRef.current = Object.keys(next).some((field) => next[field] !== latestSavedRef.current[field]);
    failedRef.current = false;
    showError('');
    setFields(next);
    setMessage(dirtyRef.current ? 'Unsaved changes' : 'Saved');
    if (timerRef.current) clearTimeout(timerRef.current);
    if (dirtyRef.current) timerRef.current = setTimeout(() => { void saveRef.current(); }, 900);
  };

  const saveNow = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = null;
    failedRef.current = false; // deliberate retry, including after a server conflict
    void saveRef.current();
  };

  return (
    <div className="clinical-note-attached-daily" style={{ display: 'grid', gap: 14 }}>
      <div className="clinical-attached-daily-heading">
        <strong>Attached Daily Note — Same Date of Service</strong>
        <p>This attachment belongs to the {parentLabel}, uses the same provider signature,
          and is not a separate authorized visit or superbill.</p>
      </div>
      {error && <div role="alert" style={{ color: '#b91c1c' }}>
        {error}
        {!disabled && dirtyRef.current && <button type="button" onClick={saveNow} disabled={saving} style={{ marginLeft: 10 }}>Retry Save</button>}
      </div>}
      {loading ? <p>Loading attached Daily Note…</p> : <>
        <details className="clinical-attached-daily-details">
          <summary>View parent measurements (Pain, ROM, Strength)</summary>
          <ReadOnlyMeasurements detail={detail} loading={false} />
        </details>
        {[
          ['subjective', 'Subjective'],
          ['assessment', 'Assessment / Diagnosis'],
          ['plan', 'Plan'],
        ].map(([key, label]) => (
          <section key={key} className="clinical-attached-daily-narrative">
            <button type="button" className="clinical-attached-daily-toggle"
              aria-expanded={Boolean(expandedNarrative[key])}
              aria-controls={`attached-daily-${key}`}
              onClick={() => setExpandedNarrative((previous) => ({ ...previous, [key]: !previous[key] }))}>
              <span>{label}</span>
              <span className="clinical-attached-daily-toggle-detail">
                {!expandedNarrative[key] && fields[key] ? `${fields[key].slice(0, 75)}${fields[key].length > 75 ? '…' : ''}` : ''}
                <span aria-hidden="true">{expandedNarrative[key] ? '−' : '+'}</span>
              </span>
            </button>
            <div id={`attached-daily-${key}`} hidden={!expandedNarrative[key]}>
              <label className="clinical-attached-daily-field" htmlFor={`attached-daily-input-${key}`}>
                <span className="clinical-attached-daily-sr-only">{label}</span>
                <textarea id={`attached-daily-input-${key}`}
                  value={fields[key]}
                  disabled={disabled || !readyRef.current}
                  onChange={(event) => updateField(key, event.target.value)}
                  onBlur={saveNow}
                  rows={key === 'assessment' ? 4 : 3}
                  maxLength={10000}
                />
              </label>
            </div>
          </section>
        ))}
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          {!disabled && <button type="button" onClick={saveNow} disabled={saving || !dirtyRef.current}>
            {saving ? 'Saving…' : 'Save Attached Daily Note'}
          </button>}
          <span aria-live="polite" style={{ fontSize: 13, color: '#475569' }}>
            {saving ? 'Saving…' : message}
          </span>
          <button type="button" onClick={() => setReloadToken((n) => n + 1)} disabled={saving || dirtyRef.current || !readyRef.current}>
            Refresh measurements
          </button>
        </div>
      </>}
    </div>
  );
}
